import type { FeedbackAnswerQuestion, QuestionData, QuestionSerialized } from '@repo/types/questions/Question';
import type { QuizAttemptPrivate, QuizPrivate, QuizSection } from '@repo/types/quiz';
import type { Dispatch, SetStateAction } from 'react';

import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import type { CarouselApi } from '@/components/ui/carousel';

import QuizAnswerResultSplit from '@/components/features/quiz/results/QuizAnswerResultSplit';
import QuizQuestionResultCard from '@/components/features/quiz/results/QuizQuestionResultCard';
import { Button } from '@/components/ui/button';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import { useQuizResultStats } from '@/hooks/quiz';

interface QuizResultDataProps {
  quiz: QuizPrivate;
  attempt: QuizAttemptPrivate;
  selectedIdx: number | null;
  setSelectedIdx: Dispatch<SetStateAction<number | null>>;
}

export default function QuizResultData(quizResultDataProps: QuizResultDataProps) {
  const { quiz, attempt, selectedIdx, setSelectedIdx } = quizResultDataProps;
  const [sectionsCarouselApi, setSectionsCarouselApi] = useState<CarouselApi>();

  const selectedSection: QuizSection | undefined = selectedIdx != null ? quiz.content.at(selectedIdx) : undefined;

  const resultData = useQuizResultStats({
    quiz,
    attempt: attempt,
    selectedIdx,
  });

  const sectionSelectorLabel = useMemo<string>(() => {
    if (selectedIdx == null) {
      return 'Cały quiz';
    }

    const section: QuizSection | undefined = quiz.content.at(selectedIdx);
    if (!section) {
      return 'Cały quiz';
    }

    return section.name ? `${selectedIdx + 1}. ${section.name}` : `Sekcja ${selectedIdx + 1}`;
  }, [quiz.content, selectedIdx]);

  useEffect(() => {
    if (!sectionsCarouselApi) {
      return;
    }

    const targetSlide: number = selectedIdx == null ? 0 : selectedIdx + 1;
    sectionsCarouselApi.scrollTo(targetSlide);
  }, [sectionsCarouselApi, selectedIdx]);

  const questionsToDisplay = useMemo<{ q: QuestionData; idx: number; sectionIdx: number }[]>(() => {
    if (selectedIdx != null) {
      return (
        quiz.content[selectedIdx]?.questions.map((q, idx) => ({
          q,
          idx,
          sectionIdx: selectedIdx,
        })) ?? []
      );
    }
    return quiz.content.flatMap((section, sectionIdx) => section.questions.map((q, idx) => ({ q, idx, sectionIdx })));
  }, [quiz.content, selectedIdx]);

  const totalQuestionCount = useMemo<number>(() => {
    if (selectedSection) {
      return selectedSection.questions.length;
    }

    return quiz.content.reduce((sum, section) => sum + section.questions.length, 0);
  }, [quiz.content, selectedSection]);

  const answeredQuestionCount = useMemo<number>(() => {
    return questionsToDisplay.reduce((sum, { idx, sectionIdx }) => {
      const answer = attempt.body.answers?.at(sectionIdx)?.at(idx);

      if (Array.isArray(answer)) {
        return answer.length > 0 ? sum + 1 : sum;
      }

      return answer != null ? sum + 1 : sum;
    }, 0);
  }, [attempt.body.answers, questionsToDisplay]);

  const scorePercentage = useMemo<number>(() => {
    if (resultData.maxScore <= 0) {
      return 0;
    }

    return Math.round((resultData.attemptScore / resultData.maxScore) * 100);
  }, [resultData.attemptScore, resultData.maxScore]);

  return (
    <div className="bg-background h-fit w-full gap-4 rounded-2xl border">
      <div className="border-b p-4">
        <h5 className="text-base font-semibold">Wynik</h5>
      </div>
      <div className="flex flex-col gap-6 p-4">
        <div className="flex flex-row gap-4">
          <div className="flex w-full flex-col items-center">
            <span className="text-muted-foreground text-xs font-medium">Wynik</span>
            <span className="text-sm font-medium">{scorePercentage}%</span>
          </div>
          <div className="flex w-full flex-col items-center">
            <span className="text-muted-foreground text-xs font-medium">Punkty</span>
            <span className="text-sm font-medium">
              {resultData.attemptScore} <span className="text-muted-foreground"> / {resultData.maxScore}</span>
            </span>
          </div>
          <div className="flex w-full flex-col items-center">
            <span className="text-muted-foreground text-xs font-medium">Udzielono</span>
            <span className="text-sm font-medium">
              {answeredQuestionCount} <span className="text-muted-foreground">/ {totalQuestionCount}</span>
            </span>
          </div>
        </div>
        {/* section selector */}
        <div className="flex flex-col gap-2 text-sm">
          <div className="text-muted-foreground text-xs font-medium">Wybrano: {sectionSelectorLabel}</div>
          <Carousel
            setApi={setSectionsCarouselApi}
            className="w-full px-10"
            opts={{
              align: 'start',
              containScroll: 'trimSnaps',
              dragFree: true,
            }}
          >
            <CarouselContent className="-ml-2">
              <CarouselItem className="basis-auto pl-2">
                <Button type="button" variant={selectedIdx == null ? 'tertiary' : 'outline'} size="sm" className="whitespace-nowrap" onClick={() => setSelectedIdx(null)}>
                  Cały quiz
                </Button>
              </CarouselItem>
              {quiz.content.map((section, idx) => {
                const sectionLabel: string = section.name ? `${idx + 1}. ${section.name}` : `Sekcja ${idx + 1}`;

                return (
                  <CarouselItem key={idx} className="basis-auto pl-2">
                    <Button type="button" variant={selectedIdx === idx ? 'tertiary' : 'outline'} size="sm" className="whitespace-nowrap" onClick={() => setSelectedIdx(idx)}>
                      {sectionLabel}
                    </Button>
                  </CarouselItem>
                );
              })}
            </CarouselContent>
            <CarouselPrevious type="button" variant="outline" size="sm" className="left-0 h-9 w-9 p-0" aria-label="Poprzednia sekcja">
              <ChevronLeftIcon />
            </CarouselPrevious>
            <CarouselNext type="button" variant="outline" size="sm" className="right-0 h-9 w-9 p-0" aria-label="Następna sekcja">
              <ChevronRightIcon />
            </CarouselNext>
          </Carousel>
        </div>
        <div className="flex h-fit md:flex-col">
          <div className="flex flex-row flex-wrap gap-2 text-nowrap">
            {questionsToDisplay.map(({ q, idx, sectionIdx }) => (
              <QuizQuestionResultCard
                key={`${sectionIdx}-${idx}`}
                idx={idx}
                sectionIdx={sectionIdx}
                question={q}
                answer={(attempt.body.answers?.at(sectionIdx)?.at(idx) as QuestionSerialized['selectedAnswer']) ?? null}
                marked={attempt.body.marked?.at(sectionIdx)?.at(idx) as FeedbackAnswerQuestion}
              />
            ))}
          </div>
        </div>
        <div className="h-full">
          <QuizAnswerResultSplit
            correct={resultData.correct}
            incorrect={resultData.incorrect}
            partially={resultData.partially}
            unmarked={resultData.unmarked}
            unanswered={resultData.unanswered}
            questionCnt={totalQuestionCount}
          />
        </div>
      </div>
    </div>
  );
}
